import type { AdditionalChainSection } from '../additionalChainUtils';
import type { BoneAssignmentRoleState } from '../types';
import AdditionalChainAddCard from './AdditionalChainAddCard';
import AdditionalDiagramCardFrame from './AdditionalDiagramCardFrame';
import BoneAssignmentCategorySection from './BoneAssignmentCategorySection';
import BoneAssignmentDiagram from './BoneAssignmentDiagram';

interface AdditionalRoleDiagramSectionProps {
  section: AdditionalChainSection;
  assignedRoleNames: ReadonlySet<string>;
  roleAssignments: ReadonlyMap<string, BoneAssignmentRoleState>;
  focusedRoleName: string | null;
  pendingRoleName: string | null;
  selectedBoneRef: string | null;
  roleAssignmentEnabled: boolean;
  formatRoleLabel: (roleName: string) => string;
  onRoleClick: (roleName: string) => void;
  onHoveredRoleChange: (roleName: string | null) => void;
  onRoleClear?: (roleName: string) => void;
  onAddChain: (chainName: string) => void;
  onChainClear?: (chainNames: readonly string[]) => void;
}

export default function AdditionalRoleDiagramSection({
  section,
  assignedRoleNames,
  roleAssignments,
  focusedRoleName,
  pendingRoleName,
  selectedBoneRef,
  roleAssignmentEnabled,
  formatRoleLabel,
  onRoleClick,
  onHoveredRoleChange,
  onRoleClear,
  onAddChain,
  onChainClear,
}: AdditionalRoleDiagramSectionProps) {
  const visibleFamilies = section.families.filter(
    (familyGroup) => familyGroup.instances.length > 0 || familyGroup.nextChainName !== null,
  );
  const visibleSubsections = section.subsections
    .map((subsection) => ({
      ...subsection,
      families: subsection.families.filter(
        (familyGroup) =>
          familyGroup.instances.length > 0 || familyGroup.nextChainName !== null,
      ),
    }))
    .filter((subsection) => subsection.families.length > 0);
  if (visibleFamilies.length === 0 && visibleSubsections.length === 0) return null;

  const sectionRoleNames = [
    ...visibleFamilies.flatMap((familyGroup) =>
      familyGroup.instances.flatMap((instance) => instance.roleNames),
    ),
    ...visibleSubsections.flatMap((subsection) =>
      subsection.families.flatMap((familyGroup) =>
        familyGroup.instances.flatMap((instance) => instance.roleNames),
      ),
    ),
  ];
  const defaultOpen =
    section.defaultOpen ||
    (focusedRoleName !== null && sectionRoleNames.includes(focusedRoleName)) ||
    (pendingRoleName !== null && sectionRoleNames.includes(pendingRoleName)) ||
    sectionRoleNames.some(
      (roleName) => (roleAssignments.get(roleName)?.boneRef ?? null) !== null,
    );

  return (
    <BoneAssignmentCategorySection
      key={`${section.key}-${defaultOpen ? 'open' : 'closed'}`}
      title={section.label}
      defaultOpen={defaultOpen}
    >
      <div className="flex flex-col gap-3 pt-3">
        {visibleFamilies.map((familyGroup) => (
          <div key={familyGroup.family.key}>
            <div className="grid grid-cols-2 gap-1.5">
              {familyGroup.instances.map((instance) => (
                <AdditionalDiagramCardFrame
                  key={instance.roleDiagram.key}
                  displayLabel={instance.displayLabel}
                  removableChainNames={instance.removableChainNames}
                  onChainClear={onChainClear}
                >
                  <BoneAssignmentDiagram
                    mode="roles"
                    diagram={instance.roleDiagram}
                    assignedRoleNames={assignedRoleNames}
                    roleAssignments={roleAssignments}
                    focusedRoleName={focusedRoleName}
                    pendingRoleName={pendingRoleName}
                    selectedBoneRef={selectedBoneRef}
                    assignmentEnabled={roleAssignmentEnabled}
                    formatRoleLabel={formatRoleLabel}
                    onRoleClick={onRoleClick}
                    onHoveredRoleChange={onHoveredRoleChange}
                    onRoleClear={onRoleClear}
                  />
                </AdditionalDiagramCardFrame>
              ))}
              {familyGroup.nextChainName && (
                <AdditionalChainAddCard
                  familyLabel={familyGroup.family.label}
                  chainName={familyGroup.nextChainName}
                  disabled={!roleAssignmentEnabled}
                  onAddChain={onAddChain}
                />
              )}
            </div>
          </div>
        ))}
        {visibleSubsections.map((subsection) => {
          const subsectionRoleNames = subsection.families.flatMap((familyGroup) =>
            familyGroup.instances.flatMap((instance) => instance.roleNames),
          );
          const subsectionDefaultOpen =
            subsection.defaultOpen ||
            (focusedRoleName !== null &&
              subsectionRoleNames.includes(focusedRoleName)) ||
            (pendingRoleName !== null &&
              subsectionRoleNames.includes(pendingRoleName)) ||
            subsectionRoleNames.some(
              (roleName) => (roleAssignments.get(roleName)?.boneRef ?? null) !== null,
            );

          return (
            <BoneAssignmentCategorySection
              key={`${section.key}-${subsection.key}-${subsectionDefaultOpen ? 'open' : 'closed'}`}
              title={subsection.label}
              defaultOpen={subsectionDefaultOpen}
            >
              <div className="flex flex-col gap-3 pt-3">
                {subsection.families.map((familyGroup) => (
                  <div key={familyGroup.family.key}>
                    <div className="grid grid-cols-2 gap-1.5">
                      {familyGroup.instances.map((instance) => (
                        <AdditionalDiagramCardFrame
                          key={instance.roleDiagram.key}
                          displayLabel={instance.displayLabel}
                          removableChainNames={instance.removableChainNames}
                          onChainClear={onChainClear}
                        >
                          <BoneAssignmentDiagram
                            mode="roles"
                            diagram={instance.roleDiagram}
                            assignedRoleNames={assignedRoleNames}
                            roleAssignments={roleAssignments}
                            focusedRoleName={focusedRoleName}
                            pendingRoleName={pendingRoleName}
                            selectedBoneRef={selectedBoneRef}
                            assignmentEnabled={roleAssignmentEnabled}
                            formatRoleLabel={formatRoleLabel}
                            onRoleClick={onRoleClick}
                            onHoveredRoleChange={onHoveredRoleChange}
                            onRoleClear={onRoleClear}
                          />
                        </AdditionalDiagramCardFrame>
                      ))}
                      {familyGroup.nextChainName && (
                        <AdditionalChainAddCard
                          familyLabel={familyGroup.family.label}
                          chainName={familyGroup.nextChainName}
                          disabled={!roleAssignmentEnabled}
                          onAddChain={onAddChain}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </BoneAssignmentCategorySection>
          );
        })}
      </div>
    </BoneAssignmentCategorySection>
  );
}
